import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { sortBy, isShallowEqual, nonNullable } from 'lib/array/arrayUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { isExperienceItem } from 'lib/checkout/checkoutUtils'
import type {
  CartableFlightSavedItemData,
  LeFlightSavedItemData,
  TransportOption,
  TripItineraryData,
  TripItineraryItem,
  TripItemWithoutDay,
} from 'tripPlanner/api/types'
import { isHotelSavedData, isExperienceSavedData, type ExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import {
  isExperienceBuyNowBookLaterBookingType,
  getCartPanelExperienceCartTime,
  resolveCartPanelItemCartStatus,
  resolveCartPanelExperienceDisplayStatus,
  type CartPanelExperienceTicket,
  type CartPanelItemCartStatus,
} from '../utils/cartPanelUtils'
import {
  extractFlightCartPanelOptions,
  type ExtractedFlightCartPanelOption,
} from '../utils/flightCartPanelUtils'
import type { ResolvedItemPricing, PricingInput } from './useItineraryItemPricing'
import { useItineraryItemPricing } from './useItineraryItemPricing'
import { useExperienceCartItemPricing, type ExperiencePricingInput } from './useExperienceCartItemPricing'
import { useFlightCartExpiryTick } from './useFlightCartExpiryTick'

export interface CartPanelItemPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
}

interface CartPanelBaseItem {
  key: string
  source: 'cart' | 'itinerary'
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  pricing: CartPanelItemPricing
  includeInTotal: boolean
  cartItemId: string | undefined
  tripItemId: number
  cartItem: App.MultiCartItem | undefined
}

export interface CartPanelAccommodationItem extends CartPanelBaseItem {
  productType: 'accommodation'
  dates: string | undefined
  duration: number | undefined
  roomName: string | undefined
  occupancy: string | undefined
  packageAutoSelected: boolean | undefined
  availableDurations: Array<number> | undefined
  offerId: string | undefined
}

export interface CartPanelExperienceItem extends CartPanelBaseItem {
  productType: 'experience'
  experienceId: string
  date: string | undefined
  itineraryDate: string | undefined
  time: string | undefined
  tickets: Array<CartPanelExperienceTicket>
  locationName: string | undefined
  bookingType: string | undefined
  isBuyNowBookLater: boolean
}

export interface CartPanelFlightItem extends CartPanelBaseItem {
  productType: 'flight'
  pairedTripItemIds: Array<number>
  returnTripItemId: number | undefined
  option: TransportOption
  returnOption: TransportOption | undefined
  savedItemData: CartableFlightSavedItemData
  returnSavedItemData: LeFlightSavedItemData | undefined
  flightCartItem: App.MultiCartFlightItem | undefined
  isAvailabilityBlocked: boolean
  hasRefreshFailed: boolean
}

export type CartPanelItem = CartPanelAccommodationItem | CartPanelExperienceItem | CartPanelFlightItem

interface UseCartPanelItemsArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  optimisticExclusions: Record<number, boolean>
  refreshFailedFlightKeys: Record<string, boolean>
}

interface UseCartPanelItemsReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  isError: boolean
  refetch: () => void
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface ExtractedAccommodationItem {
  productType: 'accommodation'
  tripItemId: number
  offerId: string
  packageId: string | undefined
  cartItemId: string | undefined
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  duration: number | undefined
  roomName: string | undefined
  roomRateId: string | undefined
  occupancy: string | undefined
  rawOccupancy: App.Occupants | undefined
  packageAutoSelected: boolean | undefined
  availableDurations: Array<number> | undefined
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
}

interface ExtractedExperienceItem {
  productType: 'experience'
  tripItemId: number
  experienceId: string
  cartItemId: string | undefined
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  isBooked: boolean
  name: string
  imageId: string | undefined
  date: string | undefined
  itineraryDate: string | undefined
  time: string | undefined
  tickets: Array<CartPanelExperienceTicket>
  locationName: string | undefined
  pickupPointId: string | undefined
  redemptionLocationId: string | undefined
  isBuyNowBookLater: boolean
  bookingType: string | undefined
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
}

type ExtractedCartPanelItem = ExtractedAccommodationItem | ExtractedExperienceItem
type MultiCartItemsData = Awaited<ReturnType<typeof getMultiCartItems>>

type ItineraryItem = TripItineraryItem | TripItemWithoutDay

interface CartPanelFlightRefreshKeyInput {
  tripItemId: number
  cartItemId: string | undefined
}

export function getCartPanelFlightRefreshKey(item: CartPanelFlightRefreshKeyInput): string {
  return item.cartItemId ?? `${item.tripItemId}`
}

function toExperienceTickets(savedData: ExperienceSavedData): Array<CartPanelExperienceTicket> {
  return (savedData.tickets ?? []).map(ticket => ({
    ticketId: ticket.ticketId,
    name: ticket.name,
    count: ticket.count,
    date: ticket.date ?? savedData.date,
  }))
}

function isExperienceDateRequired(bookingType: string | undefined): boolean {
  return bookingType === 'CALENDAR-TIMESLOTS' || bookingType === 'CALENDAR-NO-TIMESLOTS'
}

function getExperienceCartStatus(
  item: ItineraryItem,
  savedData: ExperienceSavedData,
): CartPanelItemCartStatus {
  const resolved = resolveCartPanelItemCartStatus(item.cartStatus)
  const isBuyNowBookLater = isExperienceBuyNowBookLaterBookingType(savedData.bookingType)
  if (resolved === 'active' && !savedData.date && !isBuyNowBookLater && isExperienceDateRequired(savedData.bookingType)) {
    return 'needs_dates'
  }
  if (resolved === 'active' && savedData.bookingType === 'CALENDAR-TIMESLOTS' && savedData.date && !savedData.time && !isBuyNowBookLater) {
    return 'needs_time'
  }
  return resolved ?? 'pending_hydration'
}

function extractItineraryBookableItems(
  itineraryData: TripItineraryData | undefined,
): Array<ExtractedCartPanelItem> {
  if (!itineraryData?.stops) return []

  const results: Array<ExtractedCartPanelItem> = []
  const seenItemIds = new Set<number>()

  const processItem = (item: ItineraryItem, itineraryDate?: string) => {
    if (item.isBooked) return
    if (seenItemIds.has(item.id)) return

    const savedData = item.savedItemData

    if (item.type === 'ACCOMMODATION' && isHotelSavedData(savedData)) {
      seenItemIds.add(item.id)
      const status = resolveCartPanelItemCartStatus(item.cartStatus) ?? 'pending_hydration'

      results.push({
        productType: 'accommodation',
        tripItemId: item.id,
        offerId: savedData.offerId,
        packageId: savedData.packageId,
        cartItemId: item.cartItemId,
        cartStatus: status,
        cartExcluded: item.cartExcluded ?? false,
        isBooked: item.isBooked ?? false,
        name: item.name ?? '',
        imageId: savedData.imageIds?.[0],
        checkIn: savedData.checkIn,
        checkOut: savedData.checkOut,
        duration: savedData.packageDuration,
        roomName: savedData.packageName ?? savedData.roomTypeName,
        roomRateId: savedData.roomRateId,
        occupancy: savedData.occupancies?.[0] ? formatOccupantsShort(savedData.occupancies[0]) : undefined,
        rawOccupancy: savedData.occupancies?.[0],
        packageAutoSelected: savedData.packageAutoSelected,
        availableDurations: savedData.availableDurations ?? item.availableDurations,
        savedPrice: savedData.price,
        savedMemberPrice: savedData.memberPrice,
        savedValue: savedData.value,
      })
      return
    }

    if (item.type === 'EXPERIENCE' && isExperienceSavedData(savedData)) {
      seenItemIds.add(item.id)
      results.push({
        productType: 'experience',
        tripItemId: item.id,
        experienceId: savedData.experienceId,
        cartItemId: item.cartItemId,
        cartStatus: getExperienceCartStatus(item, savedData),
        cartExcluded: item.cartExcluded ?? false,
        isBooked: item.isBooked ?? false,
        name: item.name ?? '',
        imageId: savedData.imageIds?.[0],
        date: savedData.date,
        itineraryDate,
        time: savedData.time,
        tickets: toExperienceTickets(savedData),
        locationName: item.itemPlaceDetail?.primaryTypeDisplayName ?? savedData.location?.city,
        pickupPointId: savedData.pickupPointId,
        redemptionLocationId: savedData.redemptionLocationId,
        isBuyNowBookLater: isExperienceBuyNowBookLaterBookingType(savedData.bookingType),
        bookingType: savedData.bookingType,
        savedPrice: savedData.price,
        savedMemberPrice: savedData.memberPrice,
        savedValue: savedData.value,
      })
    }
  }

  for (const stop of itineraryData.stops) {
    for (const day of stop.days) {
      for (const item of day.items) {
        processItem(item, day.date)
      }
    }
    for (const item of stop.itemsWithoutDay ?? []) {
      processItem(item)
    }
  }

  return results
}

function toPricingInput(item: ExtractedAccommodationItem): PricingInput {
  return {
    tripItemId: item.tripItemId,
    offerId: item.offerId,
    packageId: item.packageId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancy: item.rawOccupancy,
    duration: item.duration,
    roomRateId: item.roomRateId,
    savedPrice: item.savedPrice,
    savedMemberPrice: item.savedMemberPrice,
    savedValue: item.savedValue,
  }
}

function toExperiencePricingInput(
  item: ExtractedExperienceItem,
  cartItem: App.MultiCartItem | undefined,
): ExperiencePricingInput {
  return {
    tripItemId: item.tripItemId,
    experienceId: item.experienceId,
    date: item.date,
    time: cartItem ? getCartPanelExperienceCartTime(cartItem) : item.time,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    isBuyNowBookLater: item.isBuyNowBookLater,
    tickets: item.tickets,
    savedPrice: item.savedPrice,
    savedMemberPrice: item.savedMemberPrice,
    savedValue: item.savedValue,
  }
}

function getCartItemSource(cartItem: App.MultiCartItem | undefined): 'cart' | 'itinerary' {
  return cartItem ? 'cart' : 'itinerary'
}

function toCartItemsMap(
  cartData: MultiCartItemsData | undefined,
  multiCartId: string | undefined,
): Map<string, App.MultiCartItem> {
  if (!cartData || !multiCartId) return new Map<string, App.MultiCartItem>()
  const cartItems = cartData[multiCartId]
  if (!cartItems) return new Map<string, App.MultiCartItem>()
  return new Map(
    Object.values<App.MultiCartItem>(cartItems)
      .filter(item => item.state !== 'deleted')
      .map(item => [item.id, item]),
  )
}

function toAccommodationCartSyncSignature(item: ExtractedAccommodationItem): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: item.productType,
    tripItemId: item.tripItemId,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
    offerId: item.offerId,
    packageId: item.packageId,
    roomRateId: item.roomRateId,
    duration: item.duration,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    occupancy: item.rawOccupancy,
  })
}

function toExperienceCartSyncSignature(item: ExtractedExperienceItem): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: item.productType,
    tripItemId: item.tripItemId,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
    experienceId: item.experienceId,
    date: item.date,
    time: item.time,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    tickets: sortBy(
      item.tickets.map(ticket => ({
        ticketId: ticket.ticketId,
        name: ticket.name,
        count: ticket.count,
        date: ticket.date,
      })),
      ticket => `${ticket.ticketId}|${ticket.name}|${ticket.date ?? ''}`,
      'asc',
    ),
  })
}

function toItineraryCartSyncSignature(item: ExtractedCartPanelItem): string | undefined {
  if (item.productType === 'accommodation') return toAccommodationCartSyncSignature(item)
  return toExperienceCartSyncSignature(item)
}

function toFlightCartSyncSignature(item: ExtractedFlightCartPanelOption): string | undefined {
  if (!item.cartItemId) return undefined
  return JSON.stringify({
    productType: 'flight',
    tripItemId: item.tripItemId,
    returnTripItemId: item.returnTripItemId,
    pairedTripItemIds: item.pairedTripItemIds,
    cartItemId: item.cartItemId,
    cartStatus: item.cartStatus,
    cartExcluded: item.cartExcluded,
  })
}

function mapAccommodationItem(
  item: ExtractedAccommodationItem,
  pricing: ResolvedItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
): CartPanelAccommodationItem {
  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'accommodation',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    dates: item.checkIn && item.checkOut
      ? formatDynamicDateRange(item.checkIn, item.checkOut)
      : undefined,
    duration: item.duration,
    roomName: item.roomName,
    occupancy: item.occupancy,
    packageAutoSelected: item.packageAutoSelected,
    availableDurations: item.availableDurations,
    pricing,
    includeInTotal: true,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    offerId: item.offerId,
    cartItem,
  }
}

function mapExperienceItem(
  item: ExtractedExperienceItem,
  pricing: CartPanelItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
): CartPanelExperienceItem {
  const cartStatus = resolveCartPanelExperienceDisplayStatus({
    cartStatus: item.cartStatus,
    bookingType: item.bookingType,
    isBuyNowBookLater: item.isBuyNowBookLater,
    isGift: cartItem?.type === 'experience' ? cartItem.isGift : false,
    cartItem,
    hasSelectedTickets: item.tickets.length > 0,
    isAvailable: pricing.isAvailable,
  })
  const time = cartItem ? getCartPanelExperienceCartTime(cartItem) : item.time

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus,
    cartExcluded,
    productType: 'experience',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    pricing,
    includeInTotal: true,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem,
    experienceId: item.experienceId,
    date: item.date,
    itineraryDate: item.itineraryDate,
    time,
    tickets: item.tickets,
    locationName: item.locationName,
    bookingType: item.bookingType,
    isBuyNowBookLater: item.isBuyNowBookLater,
  }
}

function getOptimisticFlightExclusion(
  item: ExtractedFlightCartPanelOption,
  optimisticExclusions: Record<number, boolean>,
): boolean {
  for (const tripItemId of item.pairedTripItemIds) {
    const optimisticValue = optimisticExclusions[tripItemId]
    if (optimisticValue !== undefined) return optimisticValue
  }

  return item.cartExcluded
}

function mapFlightItem(
  item: ExtractedFlightCartPanelOption,
  cartExcluded: boolean,
  hasRefreshFailed: boolean,
): CartPanelFlightItem {
  const price = item.cartStatus === 'active' && item.cartPrice !== undefined
    ? item.cartPrice
    : item.savedPrice

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(item.cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'flight',
    isBooked: false,
    name: item.option.label ?? 'Flight',
    imageId: item.savedItemData.carrierLogo,
    pricing: {
      price,
      memberPrice: undefined,
      value: undefined,
      isAvailable: !item.isAvailabilityBlocked,
      loading: false,
      error: false,
    },
    includeInTotal: item.includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem: item.cartItem,
    pairedTripItemIds: item.pairedTripItemIds,
    returnTripItemId: item.returnTripItemId,
    option: item.option,
    returnOption: item.returnOption,
    savedItemData: item.savedItemData,
    returnSavedItemData: item.returnSavedItemData,
    flightCartItem: item.cartItem,
    isAvailabilityBlocked: item.isAvailabilityBlocked,
    hasRefreshFailed,
  }
}

function getCartItem(cartItemsMap: Map<string, App.MultiCartItem>, item: ExtractedCartPanelItem): App.MultiCartItem | undefined {
  const cartItem = item.cartItemId ? cartItemsMap.get(item.cartItemId) : undefined
  if (!cartItem) return undefined
  if (item.productType !== 'experience') return cartItem
  return isExperienceItem(cartItem.item) ? cartItem : undefined
}

export function useCartPanelItems({
  multiCartId,
  itineraryData,
  optimisticExclusions,
  refreshFailedFlightKeys,
}: UseCartPanelItemsArgs): UseCartPanelItemsReturn {
  const queryClient = useQueryClient()

  const { data: cartData, isLoading: cartLoading, isError: cartError, refetch } = useQuery({
    queryKey: ['tripPlannerCartItems', multiCartId],
    queryFn: () => getMultiCartItems(multiCartId!),
    enabled: !!multiCartId,
    refetchOnMount: true,
  })

  const itineraryItems = useMemo(
    () => extractItineraryBookableItems(itineraryData),
    [itineraryData],
  )

  const accommodationItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedAccommodationItem => item.productType === 'accommodation'),
    [itineraryItems],
  )

  const experienceItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedExperienceItem => item.productType === 'experience'),
    [itineraryItems],
  )

  const pricingInputs = useMemo<Array<PricingInput>>(
    () => accommodationItems.map(toPricingInput),
    [accommodationItems],
  )

  const cartItemsMap = useMemo(() => toCartItemsMap(cartData, multiCartId), [cartData, multiCartId])

  const refetchCartItems = useCallback(async() => {
    const result = await refetch()
    return toCartItemsMap(result.data, multiCartId)
  }, [multiCartId, refetch])
  const flightCartNow = useFlightCartExpiryTick(cartItemsMap)

  const experiencePricingInputs = useMemo<Array<ExperiencePricingInput>>(
    () => experienceItems.map(item => toExperiencePricingInput(item, getCartItem(cartItemsMap, item))),
    [experienceItems, cartItemsMap],
  )

  const pricing = useItineraryItemPricing(pricingInputs)
  const experiencePricing = useExperienceCartItemPricing(experiencePricingInputs)

  const flightItems = useMemo<Array<ExtractedFlightCartPanelOption>>(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => extractFlightCartPanelOptions(stop.transports, cartItemsMap, flightCartNow))
  }, [itineraryData, cartItemsMap, flightCartNow])

  const syncedCartItemSignatures = useMemo(
    () => nonNullable([
      ...itineraryItems.map(toItineraryCartSyncSignature),
      ...flightItems.map(toFlightCartSyncSignature),
    ]),
    [itineraryItems, flightItems],
  )

  const prevCartItemSignaturesRef = useRef<Array<string>>([])
  useEffect(() => {
    const currentSignatures = sortBy(syncedCartItemSignatures, signature => signature, 'asc')
    if (prevCartItemSignaturesRef.current.length > 0 && !isShallowEqual(prevCartItemSignaturesRef.current, currentSignatures)) {
      if (multiCartId) {
        queryClient.invalidateQueries({ queryKey: ['tripPlannerCartItems', multiCartId] })
      }
    }
    prevCartItemSignaturesRef.current = currentSignatures
  }, [syncedCartItemSignatures, multiCartId, queryClient])

  const items = useMemo<Array<CartPanelItem>>(() => {
    const accommodationAndExperienceItems = nonNullable(
      itineraryItems.map((item): CartPanelItem | undefined => {
        const cartItem = getCartItem(cartItemsMap, item)
        const effectiveExcluded = optimisticExclusions[item.tripItemId] ?? item.cartExcluded

        if (item.productType === 'accommodation') {
          const resolvedPricing = pricing.get(item.tripItemId)
          if (!resolvedPricing) return undefined
          return mapAccommodationItem(item, resolvedPricing, cartItem, effectiveExcluded)
        }

        const resolvedPricing = experiencePricing.get(item.tripItemId)
        if (!resolvedPricing) return undefined
        return mapExperienceItem(item, resolvedPricing, cartItem, effectiveExcluded)
      }),
    )

    const mappedFlightItems = flightItems.map(item => {
      const refreshKey = getCartPanelFlightRefreshKey(item)
      return mapFlightItem(
        item,
        getOptimisticFlightExclusion(item, optimisticExclusions),
        refreshFailedFlightKeys[refreshKey] ?? false,
      )
    })

    return [...accommodationAndExperienceItems, ...mappedFlightItems]
  }, [itineraryItems, pricing, experiencePricing, cartItemsMap, optimisticExclusions, flightItems, refreshFailedFlightKeys])

  return {
    items,
    isLoading: !!multiCartId && cartLoading,
    isError: cartError,
    refetch,
    refetchCartItems,
  }
}
